import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Gauge } from "lucide-react";

/**
 * Compact monthly-proposal usage meter for the sidebar.
 *
 *   Reads GET /api/usage/current and renders a small progress pill:
 *     ┌──────────────────────────────────────┐
 *     │  PROPOSALS · this month              │
 *     │  ▓▓▓▓▓▓▓░░░  7 / 10                  │
 *     │  Upgrade →                            │
 *     └──────────────────────────────────────┘
 *
 * Auto-refreshes every 60s AND whenever another component dispatches the
 * `bidforge:usage-refresh` window event (fires from OverageDialog after a
 * successful overage-accepted generate).  Unlimited plans (Business/Enterprise)
 * render a compact "UNLIMITED" pill instead of a bar.
 */
export default function UsageMeter() {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/usage/current");
      setData(r.data);
    } catch {
      /* silent — meter is best-effort */
    }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);
    const onRefresh = () => load();
    window.addEventListener("bidforge:usage-refresh", onRefresh);
    return () => {
      clearInterval(iv);
      window.removeEventListener("bidforge:usage-refresh", onRefresh);
    };
  }, [load]);

  if (!data) return null;

  const {
    proposals_drafted = 0,
    cap,
    pct,
    unlimited,
    warn,
    at_cap,
    plan,
    plan_key,
    overage_this_month = 0,
  } = data;

  // Recommend the next-tier upgrade target for the UPGRADE pill destination.
  const upgradeTarget = plan_key === "starter" ? "professional" : plan_key === "professional" ? "business" : "enterprise";
  const upgradeHref = `/pricing?from=${plan_key || "starter"}&required=${upgradeTarget}&reason=monthly_cap_reached`;

  // Unlimited plan — show a subtle badge, not a bar.
  if (unlimited) {
    return (
      <Link
        to="/billing"
        data-testid="usage-meter"
        className="mb-3 flex items-center gap-2 rounded-xl px-3 py-2 bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 hover:from-emerald-100 hover:to-teal-100 transition-all"
      >
        <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-500 text-white">
          <Gauge className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500">PROPOSALS · MO</div>
          <div className="text-sm font-display font-bold text-emerald-700" data-testid="usage-meter-count">
            {proposals_drafted} <span className="text-slate-400 font-normal text-xs">· unlimited</span>
          </div>
        </div>
      </Link>
    );
  }

  const barColor = at_cap
    ? "bg-rose-500"
    : warn
    ? "bg-amber-500"
    : "bg-gradient-to-r from-violet-500 to-fuchsia-500";
  const wrapColor = at_cap
    ? "bg-rose-50 border-rose-200 hover:bg-rose-100"
    : warn
    ? "bg-amber-50 border-amber-200 hover:bg-amber-100"
    : "bg-gradient-to-br from-violet-50 to-fuchsia-50 border-violet-200 hover:from-violet-100 hover:to-fuchsia-100";
  const tileColor = at_cap
    ? "bg-rose-500 text-white"
    : warn
    ? "bg-amber-500 text-white"
    : "bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white";
  const numColor = at_cap ? "text-rose-700" : warn ? "text-amber-700" : "text-slate-900";

  return (
    <Link
      to={warn || at_cap ? upgradeHref : "/billing"}
      data-testid="usage-meter"
      className={`mb-3 flex flex-col gap-1.5 rounded-xl px-3 py-2 transition-all border ${wrapColor}`}
    >
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tileColor}`}>
          <Gauge className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500">PROPOSALS · MO</div>
          <div className={`text-sm font-display font-bold ${numColor}`} data-testid="usage-meter-count">
            {proposals_drafted} <span className="text-slate-400 font-normal">/ {cap}</span>
          </div>
        </div>
        {(warn || at_cap) && (
          <span className="text-[10px] font-mono font-bold tracking-widest text-violet-600" data-testid="usage-meter-upgrade">
            UPGRADE
          </span>
        )}
      </div>
      <div className="h-1.5 w-full bg-slate-200/60 rounded-full overflow-hidden" data-testid="usage-meter-bar">
        <div
          className={`h-full ${barColor} transition-all duration-500`}
          style={{ width: `${Math.max(2, Math.min(100, pct || 0))}%` }}
        />
      </div>
      {at_cap && (
        <div className="text-[10px] font-mono font-bold tracking-widest text-rose-700" data-testid="usage-meter-atcap">
          AT CAP · {overage_this_month > 0 ? `${overage_this_month} OVERAGE${overage_this_month === 1 ? "" : "S"}` : "NEXT IS OVERAGE"}
        </div>
      )}
      {!at_cap && warn && (
        <div className="text-[10px] font-mono font-bold tracking-widest text-amber-700" data-testid="usage-meter-warn">
          {pct}% · APPROACHING CAP
        </div>
      )}
      {!warn && !at_cap && (
        <div className="text-[10px] font-mono text-slate-500 tracking-wide">
          {plan?.toUpperCase()} · {cap - proposals_drafted} left
        </div>
      )}
    </Link>
  );
}
